import React, { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';

import { supabase } from '../lib/supabase';
import { logEvent } from '../services/loggingService';
import { getCurrentUserId } from '../utils/authUtils';
import {
  getCreditCost,
  type UserCreditOperation,
} from '../config/creditCosts';
import {
  CREDITS_UPDATED_EVENT,
  notifyCreditsChanged,
  type CreditsUpdatedDetail,
} from '../utils/creditsSync';

export interface CreditSystemState {
  credits: number | null;
  loading: boolean;
  error: string | null;
  isLowOnCredits: boolean;
  isCriticallyLowOnCredits: boolean;
  bypassEnabled: boolean;
}

export interface CreditContextValue extends CreditSystemState {
  fetchCredits: () => Promise<void>;
  /** Prefer server-side debits in edge functions; use for legacy client-only flows. */
  deductCredits: (amount: number, operation?: string) => Promise<boolean>;
  deductForOperation: (operation: UserCreditOperation, metadata?: Record<string, unknown>) => Promise<boolean>;
  addCredits: (amount: number) => Promise<boolean>;
  hasEnoughCredits: (required: number) => boolean;
}

/** Shared credit state so dropdown and other consumers don't refetch on every open. */
export const CreditContext = createContext<CreditContextValue | null>(null);

/** Single source of truth for credits; fetch once on mount. Wrap app (e.g. in AppProviders) so all useCreditSystem() consumers share cache. */
export function CreditProvider(props: { children: React.ReactNode }) {
  const value = useCreditSystemState();
  return React.createElement(CreditContext.Provider, { value }, props.children);
}

/** Prefer context (shared cache); fallback to local state when outside CreditProvider. */
export const useCreditSystem = () => {
  const ctx = useContext(CreditContext);
  if (ctx != null) return ctx;
  return useCreditSystemState();
};

function useCreditSystemState() {
  const [credits, setCredits] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bypassEnabled, setBypassEnabled] = useState(false);
  const creditsRef = useRef<number | null>(null);
  creditsRef.current = credits;

  // Fetch user credits and bypass status
  const fetchCredits = useCallback(async () => {
    try {
      // Only show loading when we have no cached value; refetches don't flash loading (e.g. dropdown open)
      setLoading(creditsRef.current === null);
      setError(null);

      const userId = await getCurrentUserId();
      if (!userId) {
        setCredits(null);
        setBypassEnabled(false);
        setLoading(false);
        return;
      }

      // FIRST: Check if this user has bypass enabled in database
      const { data: userData } = await supabase
        .from('users')
        .select('credit_bypass_enabled')
        .eq('user_id', userId)
        .maybeSingle();

      const userBypassEnabled = userData?.credit_bypass_enabled === true;
      setBypassEnabled(userBypassEnabled);

      // Try to get existing credits (bypass still reads real balance for display)
      const { data, error } = await supabase
        .from('user_credits')
        .select('credits_balance')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching credits:', error);
        // If RLS policy violation, try to create initial record
        if (error.code === '42501') {
          try {
            const { data: insertData, error: insertError } = await supabase
              .from('user_credits')
              .insert({
                user_id: userId,
                credits_balance: 5 // Free plan starting credits
              })
              .select('credits_balance')
              .single();

            if (insertError) {
              console.warn('Could not create initial credits record:', insertError);
              setCredits(5); // Free plan default
            } else {
              setCredits(Number(insertData.credits_balance));
            }
          } catch (insertErr) {
            console.warn('Insert fallback failed:', insertErr);
            setCredits(5); // Free plan default
          }
        } else {
          setCredits(5); // Free plan default for other errors
        }
      } else {
        setCredits(Number(data?.credits_balance ?? 5));
      }

    } catch (err) {
      console.error('Error in fetchCredits:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch credits');
      setCredits(5); // Free plan default
    } finally {
      setLoading(false);
    }
  }, []);

  // Deduct credits via unified RPC (ledger + balance)
  const deductCredits = useCallback(async (
    amount: number,
    operation = 'client_debit',
    metadata: Record<string, unknown> = {},
  ) => {
    let previousBalance: number | null = null;
    try {
      console.log('💳 Deducting credits:', { amount, currentCredits: credits });
      
      // BYPASS: If this user has bypass enabled, skip deduction
      if (bypassEnabled) {
        console.log('🔓 USER-SPECIFIC BYPASS: Skipping credit deduction (no charges applied)');
        await logEvent({
          level: 'info',
          action: 'credits_deducted_bypassed',
          details: { 
            amount, 
            bypassEnabled: true,
            note: 'User has credit bypass enabled'
          }
        });
        return true;
      }

      previousBalance = creditsRef.current;
      if (previousBalance !== null) {
        const optimistic = Math.max(0, Math.round((previousBalance - amount) * 100) / 100);
        setCredits(optimistic);
        notifyCreditsChanged({ newBalance: optimistic });
      }
      
      const userId = await getCurrentUserId();
      if (!userId) {
        throw new Error('User not authenticated');
      }

      const { data: rpcData, error } = await supabase.rpc('deduct_user_credits', {
        p_user_id: userId,
        p_amount: amount,
        p_operation: operation,
        p_description: operation,
        p_metadata: metadata,
      });

      if (error) {
        console.error('Database error during credit deduction:', error);
        throw new Error(`Failed to deduct credits: ${error.message}`);
      }

      const result = rpcData as {
        ok?: boolean;
        error?: string;
        balance?: number;
        bypass?: boolean;
        deducted?: number;
        required?: number;
      } | null;

      if (!result?.ok) {
        if (previousBalance !== null) {
          setCredits(previousBalance);
          notifyCreditsChanged({ newBalance: previousBalance });
        }
        if (result?.error === 'insufficient_credits') {
          throw new Error(
            `Insufficient credits. You need ${result.required ?? amount} credits but only have ${result.balance ?? credits ?? 0}.`,
          );
        }
        throw new Error(result?.error ?? 'Failed to deduct credits');
      }

      const newBalance = Number(result.balance ?? credits ?? 0);
      setCredits(newBalance);
      notifyCreditsChanged({ newBalance });
      console.log('Credits deducted successfully:', {
        deducted: result.deducted ?? amount,
        newBalance,
        previousBalance: credits,
        bypass: result.bypass === true,
      });

      await logEvent({
        level: 'info',
        action: 'credits_deducted',
        details: { 
          amount, 
          newBalance,
          previousBalance: credits,
          userId,
          bypass: result.bypass === true,
        }
      });

      return true;

    } catch (err) {
      console.error('Error deducting credits:', err);
      const rollback = creditsRef.current;
      if (rollback !== null && previousBalance !== null && rollback !== previousBalance) {
        setCredits(previousBalance);
        notifyCreditsChanged({ newBalance: previousBalance });
      }
      setError(err instanceof Error ? err.message : 'Failed to deduct credits');
      
      await logEvent({
        level: 'error',
        action: 'credit_deduction_failed',
        details: { 
          amount,
          currentCredits: credits,
          error: err instanceof Error ? err.message : String(err)
        },
        error: err instanceof Error ? err : new Error(String(err))
      });
      
      return false;
    }
  }, [credits, bypassEnabled]);

  const deductForOperation = useCallback(async (
    operation: UserCreditOperation,
    metadata: Record<string, unknown> = {},
  ) => deductCredits(getCreditCost(operation), operation, metadata), [deductCredits]);

  // Add credits
  const addCredits = useCallback(async (amount: number) => {
    try {
      console.log('≡ƒÆ░ Adding credits:', { amount, currentCredits: credits });
      
      const userId = await getCurrentUserId();
      if (!userId) {
        throw new Error('User not authenticated');
      }

      // First, try to update existing record
      const { data, error } = await supabase
        .from('user_credits')
        .update({
          credits_balance: (credits || 0) + amount,
          last_updated: new Date().toISOString()
        })
        .eq('user_id', userId)
        .select('credits_balance')
        .single();

      if (error) {
        // If no record exists (PGRST116), create one with the added amount
        if (error.code === 'PGRST116') {
          console.log('≡ƒô¥ No credits record found, creating one...');
          
          const initialBalance = (credits || 10) + amount;
          const { data: insertData, error: insertError } = await supabase
            .from('user_credits')
            .insert({
              user_id: userId,
              credits_balance: initialBalance,
              last_updated: new Date().toISOString()
            })
            .select('credits_balance')
            .single();
            
          if (insertError) {
            console.error('Γ¥î Failed to create credits record:', insertError);
            throw new Error(`Failed to create credits record: ${insertError.message}`);
          }
          
          setCredits(insertData.credits_balance);
          console.log('Γ£à Credits record created and credits added:', {
            added: amount,
            newBalance: insertData.credits_balance,
            previousBalance: credits
          });
        } else {
          console.error('Γ¥î Database error during credit addition:', error);
          throw new Error(`Failed to add credits: ${error.message}`);
        }
      } else {
        setCredits(data.credits_balance);
        console.log('Γ£à Credits added successfully:', {
          added: amount,
          newBalance: data.credits_balance,
          previousBalance: credits
        });
      }

      await logEvent({
        level: 'info',
        action: 'credits_added',
        details: { 
          amount, 
          newBalance: data?.credits_balance || (credits || 10) + amount,
          previousBalance: credits,
          userId,
        }
      });

      const addedBalance = Number(data?.credits_balance ?? (credits || 10) + amount);
      notifyCreditsChanged({ newBalance: addedBalance });

      return true;

    } catch (err) {
      console.error('Error adding credits:', err);
      setError(err instanceof Error ? err.message : 'Failed to add credits');
      return false;
    }
  }, [credits]);

  // Check if user has enough credits
  const hasEnoughCredits = useCallback((required: number) => {
    // BYPASS: If this user has bypass enabled, always return true
    if (bypassEnabled) {
      console.log('🔓 USER-SPECIFIC BYPASS: Credit check skipped (always returns true)');
      return true;
    }

    // Credits still loading — optimistically allow through; deductCredits will
    // perform the authoritative server-side check and will fail gracefully.
    if (credits === null) {
      console.log('💳 Credit Check: credits still loading, optimistically allowing through');
      return true;
    }
    
    const result = credits >= required;
    console.log('💳 Credit Check:', {
      currentCredits: credits,
      requiredCredits: required,
      hasEnough: result
    });
    return result;
  }, [credits, bypassEnabled]);

  // Keep in sync with PlanStatus pill / server-side debits
  useEffect(() => {
    const onCreditsUpdated = (e: Event) => {
      const detail = (e as CustomEvent<CreditsUpdatedDetail>).detail;
      if (typeof detail?.newBalance === 'number' && Number.isFinite(detail.newBalance)) {
        setCredits(detail.newBalance);
        return;
      }
      if (typeof detail?.delta === 'number' && Number.isFinite(detail.delta)) {
        setCredits((c) => {
          const base = c ?? 0;
          return Math.max(0, Math.round((base + detail.delta!) * 100) / 100);
        });
      }
    };
    window.addEventListener(CREDITS_UPDATED_EVENT, onCreditsUpdated);
    return () => window.removeEventListener(CREDITS_UPDATED_EVENT, onCreditsUpdated);
  }, []);

  // Load credits on mount
  useEffect(() => {
    fetchCredits();
  }, [fetchCredits]);

  // Re-fetch credits when auth state changes (fixes race condition where
  // CreditProvider mounts before the session is available, leaving credits = null).
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
        fetchCredits();
      } else if (event === 'SIGNED_OUT') {
        setCredits(null);
        setBypassEnabled(false);
      }
    });
    return () => subscription.unsubscribe();
  }, [fetchCredits]);

  // Computed values
  const isLowOnCredits = (credits || 0) <= 5;
  const isCriticallyLowOnCredits = (credits || 0) === 0;

  return {
    credits,
    loading,
    error,
    isLowOnCredits,
    isCriticallyLowOnCredits,
    bypassEnabled,
    fetchCredits,
    deductCredits,
    deductForOperation,
    addCredits,
    hasEnoughCredits
  };
};
